import { List, Record } from 'immutable'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { Attachment, IAttachment } from './Attachment'
import { Comment, IComment } from './Comment'
import { AssignmentStatus, FeedItemType, LeadPriority, LeadType } from './enums'
import { FeedItemEditState, IFeedItemEditState } from './FeedItemEditState'
import { FeedItemSource, IFeedItemSource } from './FeedItemSource'

export interface IFeedItem {
  assignmentId?: string
  attachments?: List<IAttachment>
  author: IAssociatedContact
  comments?: List<IComment>
  createdOn?: IFeedItemSource
  creator: string
  dateCreated: number
  dateUpdated: number
  editState?: IFeedItemEditState
  hasStarred: List<string>
  id: string
  isArchived: boolean
  isComplete: boolean
  isEdited: boolean
  isEditing: boolean
  isPublic: boolean
  isSaving: boolean
  lastActivityTime: number
  leadPriority: LeadPriority
  leadType: LeadType
  markup: string
  owners: List<string>
  selectedPhoto?: string
  showComments: boolean
  status: AssignmentStatus
  tags: List<string>
  title?: string
  totalComments: number
  type: FeedItemType
}

const FeedItemRecord = Record({
  assignmentId: undefined,
  attachments: undefined,
  author: new AssociatedContact(),
  comments: undefined,
  createdOn: undefined,
  creator: '',
  dateCreated: 0,
  dateUpdated: 0,
  editState: undefined,
  hasStarred: List<string>(),
  id: '',
  isArchived: true,
  isComplete: false,
  isEdited: false,
  isEditing: false,
  isPublic: false,
  isSaving: false,
  lastActivityTime: 0,
  leadPriority: '',
  leadType: '',
  markup: '',
  owners: List<string>(),
  selectedPhoto: undefined,
  showComments: false,
  status: 'Incomplete',
  tags: List<string>(),
  title: '',
  totalComments: 0,
  type: 'Post',
})

export class FeedItem extends FeedItemRecord implements IFeedItem {
  public static fromJS(initialState?: Partial<IFeedItem>) {
    if (!initialState) {
      return new FeedItem()
    }

    const {
      attachments,
      author,
      comments,
      createdOn,
      editState,
      hasStarred,
      owners,
      tags,
      ...rest
    } = initialState
    return new FeedItem({
      attachments: attachments
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : undefined,
      author: AssociatedContact.fromJS(author),
      comments: comments
        ? List(comments.map((c) => Comment.fromJS(c)))
        : undefined,
      createdOn: createdOn ? FeedItemSource.fromJS(createdOn) : undefined,
      editState: editState ? FeedItemEditState.fromJS(editState) : undefined,
      hasStarred: List(hasStarred || []),
      owners: List(owners || []),
      tags: List(tags || []),
      ...rest,
    })
  }

  public readonly assignmentId?: string
  public readonly attachments?: List<Attachment>
  public readonly author: AssociatedContact
  public readonly comments?: List<Comment>
  public readonly createdOn: FeedItemSource
  public readonly creator: string
  public readonly dateCreated: number
  public readonly dateUpdated: number
  public readonly editState?: FeedItemEditState
  public readonly hasStarred: List<string>
  public readonly id: string
  public readonly isArchived: boolean
  public readonly isComplete: boolean
  public readonly isEdited: boolean
  public readonly isEditing: boolean
  public readonly isPublic: boolean
  public readonly isSaving: boolean
  public readonly lastActivityTime: number
  public readonly leadPriority: LeadPriority
  public readonly leadType: LeadType
  public readonly markup: string
  public readonly owners: List<string>
  public readonly selectedPhoto?: string
  public readonly showComments: boolean
  public readonly status: AssignmentStatus
  public readonly tags: List<string>
  public readonly title?: string
  public readonly totalComments: number
  public readonly type: FeedItemType

  constructor(initialState?: Partial<FeedItem>) {
    initialState ? super(initialState) : super()
  }
}
